/* maaho-guard-alert.js - tells the back office each time the made-to-measure guard stops
   a customer, and the back office e-mails the owner. Called by assets/maaho-header.js
   (the cart drawer's limit message) and assets/maaho-product.js (the limit at Add to
   cart, a size that cannot be made); the cart page's limit message
   (sections/maaho-cart.liquid, data-guard-alert-reason) is reported from here on load.

   Reads, from #cartDrawer in sections/maaho-header.liquid: data-back-office-url (the
   shop metafield maaho.back_office_url; blank switches reporting off) and
   data-market-country. The language is <html lang>.

   Fire and forget. The report goes as text/plain through navigator.sendBeacon, which
   needs no CORS preflight and survives the page being left; nothing waits on it and
   every failure is swallowed. A basket is reported once per reason per browser session
   here, and once per reason a day by the back office. */

window.MaahoGuardAlert = (function () {
  'use strict';

  /* Must match MOST_LINES in the back office's StorefrontGuardAlertController. */
  var MOST_LINES = 30;
  var SENT_STORAGE_KEY = 'maaho-guard-alert-sent';
  var TAB_TOKEN_STORAGE_KEY = 'maaho-guard-alert-tab';

  var sentThisSession = {};

  function readSession(key) {
    try { return window.sessionStorage.getItem(key); } catch (unavailable) { return null; }
  }

  function writeSession(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (unavailable) { /* private mode */ }
  }

  function alreadySent(key) {
    if (sentThisSession[key]) return true;
    var stored = readSession(SENT_STORAGE_KEY);
    return Boolean(stored) && stored.split('\n').indexOf(key) > -1;
  }

  function rememberSent(key) {
    sentThisSession[key] = true;
    var stored = readSession(SENT_STORAGE_KEY);
    writeSession(SENT_STORAGE_KEY, (stored ? stored + '\n' : '') + key);
  }

  function cartDrawer() {
    return document.getElementById('cartDrawer');
  }

  function endpoint() {
    var drawer = cartDrawer();
    var base = drawer ? (drawer.getAttribute('data-back-office-url') || '').trim() : '';
    return base ? base.replace(/\/+$/, '') + '/storefront/guard-alert' : '';
  }

  /** Free text as the back office accepts it: no control characters, at most `most` long. */
  function plainText(value, most) {
    return String(value == null ? '' : value).replace(/[\u0000-\u001f]/g, ' ').trim().slice(0, most);
  }

  function optionIdsIn(written) {
    return String(written == null ? '' : written)
      .split(',')
      .map(function (one) { return parseInt(one, 10); })
      .filter(function (one) { return isFinite(one) && one > 0; })
      .slice(0, 60);
  }

  /**
   * One line of the report.
   * @param {{title?: string, handle?: string, quantity?: number, measurements?: string,
   *          options?: string|number[], adding?: boolean}} line
   */
  function reportLine(line) {
    var handle = String(line.handle || '');
    var quantity = parseInt(line.quantity, 10);
    var options = Array.isArray(line.options) ? line.options.join(',') : line.options;
    var handleIsValid = /^[a-z0-9-]{1,200}$/.test(handle);
    // The back office needs a title or a handle on every line.
    var cleaned = {
      title: plainText(line.title, 200) || (handleIsValid ? null : 'untitled product'),
      handle: handleIsValid ? handle : null,
      quantity: isFinite(quantity) && quantity > 0 ? Math.min(quantity, 9999) : 1,
      measurements: plainText(line.measurements, 200) || null,
      options: optionIdsIn(options)
    };
    if (line.adding) cleaned.adding = true;
    return cleaned;
  }

  /** The lines of a /cart.js response. */
  function linesFromCart(cart) {
    return ((cart && cart.items) || []).map(function (item) {
      var properties = item.properties || {};
      return reportLine({
        title: item.product_title,
        handle: item.handle,
        quantity: item.quantity,
        measurements: properties._measurements,
        options: properties._options
      });
    });
  }

  /** The cart's token, else the cart cookie, else one made for this browser tab. */
  function basketToken(cart) {
    var token = cart && cart.token;
    if (!token) {
      var cookie = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
      token = cookie ? decodeURIComponent(cookie[1]) : readSession(TAB_TOKEN_STORAGE_KEY);
    }
    if (!token) {
      token = 'tab-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      writeSession(TAB_TOKEN_STORAGE_KEY, token);
    }
    return String(token).replace(/[^A-Za-z0-9_\-:?=.%]/g, '').slice(0, 200);
  }

  /** The whole report, or null when it would be refused or there is nowhere to send it. */
  function buildReport(reason, page, lines, cart) {
    var drawer = cartDrawer();
    var country = drawer ? (drawer.getAttribute('data-market-country') || '').toUpperCase() : '';
    var language = document.documentElement.lang || '';
    var token = basketToken(cart);
    if (token.length < 8) return null;

    return {
      reason: reason,
      page: page,
      token: token,
      country: /^[A-Z]{2}$/.test(country) ? country : null,
      language: /^[a-z]{2,3}(-[a-z]{2,4})?$/i.test(language) ? language : null,
      lines: lines.slice(-MOST_LINES)
    };
  }

  function post(address, body) {
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(address, body)) return;
      window.fetch(address, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain' },
        body: body
      }).catch(function () {});
    } catch (ignored) { /* never let a report break the page */ }
  }

  /**
   * Reports that the guard stopped the customer.
   * @param {'limit'|'size'} reason
   * @param {'product'|'cart'|'drawer'} page
   * @param {object|null} cart          a /cart.js response, when the page has one
   * @param {object} [lineBeingAdded]   the configuration the customer was stopped with
   */
  function report(reason, page, cart, lineBeingAdded) {
    try {
      var address = endpoint();
      if (!address) return;

      var lines = linesFromCart(cart);
      if (lineBeingAdded) lines.push(reportLine(Object.assign({ adding: true }, lineBeingAdded)));

      var built = buildReport(reason, page, lines, cart);
      if (!built) return;

      var sentKey = reason + '|' + built.token;
      if (alreadySent(sentKey)) return;
      rememberSent(sentKey);

      post(address, JSON.stringify(built));
    } catch (ignored) { /* never let a report break the page */ }
  }

  /* The cart page draws its limit message on the server, so it is reported once the
     page has loaded. */
  function reportMessageDrawnOnTheServer() {
    var message = document.querySelector('[data-guard-alert-reason]');
    if (!message || !endpoint()) return;

    window.fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        report(message.getAttribute('data-guard-alert-reason'),
          message.getAttribute('data-guard-alert-page'), cart);
      })
      .catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reportMessageDrawnOnTheServer);
  } else {
    reportMessageDrawnOnTheServer();
  }

  return {
    report: report,
    linesFromCart: linesFromCart,
    buildReport: buildReport
  };
})();
